import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { ExternalLink, ArrowLeft } from 'lucide-react';
import { BookingStatusBadge } from '../utils/booking-status';
import AdminStatusUpdate from './AdminStatusUpdate';
import { BookingDetails } from '@/hooks/bookings/types';
import { openBookingInNewTab } from '@/lib/booking-url-utils';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

interface BookingDetailsHeaderProps {
  booking: BookingDetails;
  isAdmin: boolean;
  onStatusUpdate?: () => void;
  showBackButton?: boolean;
  isInModal?: boolean;
}

export const BookingDetailsHeader = ({
  booking,
  isAdmin,
  onStatusUpdate,
  showBackButton = false,
  isInModal = false
}: BookingDetailsHeaderProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleOpenInNewTab = () => {
    openBookingInNewTab(booking.id);
    toast({
      title: "Opening booking",
      description: "Booking details opened in new tab"
    });
  };

  const handleBack = () => {
    navigate('/bookings');
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Main header content */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            {showBackButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            )}
            <h1 className="text-xl md:text-2xl font-bold text-card-foreground">
              Booking Details
            </h1>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>ID: {booking.id.substring(0, 8)}</span>
            <span>•</span>
            <span>{format(new Date(booking.created_at), 'MMM d, yyyy')}</span>
          </div>
        </div>
        
        {/* Desktop: Actions with status badge */}
        <div className="hidden md:flex items-center gap-3">
          <BookingStatusBadge status={booking.status} />
          {isAdmin && (
            <div className="flex items-center gap-2">
              {isInModal && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleOpenInNewTab} 
                  className="gap-2 h-9"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span className="hidden sm:inline">Open in New Tab</span>
                </Button>
              )}
              <AdminStatusUpdate 
                bookingId={booking.id} 
                currentStatus={booking.status} 
                onStatusUpdate={onStatusUpdate} 
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Mobile: Full-width status badge and actions */}
      <div className="md:hidden space-y-3">
        <BookingStatusBadge status={booking.status} />
        {isAdmin && (
          <div className="flex items-center gap-2 justify-center">
            {isInModal && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleOpenInNewTab} 
                className="gap-2 h-9"
              >
                <ExternalLink className="h-4 w-4" />
                Open in New Tab
              </Button>
            )}
            <AdminStatusUpdate 
              bookingId={booking.id} 
              currentStatus={booking.status} 
              onStatusUpdate={onStatusUpdate} 
            />
          </div>
        )}
      </div>
    </div>
  );
};
